import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import { organizationManagerNotificationRepo, userRepo } from "@repositories";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { textBuilder } from "@shared/helpers/text-builder";
import { Roles } from "@shared/Roles";
import { Allow, remult } from "remult";
import { User } from "../user";
import { type TimeReport } from "./entity";

export const TimeReportEntityConfig: HyperEntityConfig<TimeReport> = {
  allowApiRead: Allow.authenticated,
  allowApiCrud: Allow.authenticated,
  apiPrefilterPerRole: (filter) => {
    if (!remult.user) return;
    filter(Roles.SuperDuperAdmin, false);

    filter([Roles.SuperAdmin, Roles.Admin], {
      organizationManager: {
        $is: {
          organization: {
            $id: remult.user.organizationId,
          },
        },
      },
    });

    filter(Roles.Manager, {
      organizationManager: {
        $id: remult.user.id,
      },
    });

    filter(Roles.User, {
      associationId: {
        $in: remult.user.associations,
      },
    });
  },

  allowInsertPerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );

    allow(Roles.Admins, {
      organizationManager: {
        $is: {
          organization: {
            $id: remult.user!.organizationId,
          }
        }
      }
    });

    allow(Roles.Manager, (entity) => {
      return remult.user?.id === entity.organizationManagerId;
    });
  },

  async onInsert(entity, e) {
    const [user, manager] = await awaiter(
      userRepo.findOne({
        where: User.hyperFilter({
          associations: {
            $some: {
              id: entity.associationId,
            },
          },
        }),
      }),
      e.relations.organizationManager.findOne(),
    );

    if (!user || !manager) return;

    await organizationManagerNotificationRepo.insert({
      toId: manager.id,
      title: "דיווח שעות חדש",
      icon: NotificationIcon.CLOCK,
      content: textBuilder()
        .if(user.gender === "MALE", "המתנדב")
        .if(user.gender === "FEMALE", "המתנדבת")
        .if(user.gender === "OTHER", "המתנדב/ת")
        .space()
        .add(user.name)
        .space()
        .if(user.gender === "MALE", "הגיש")
        .if(user.gender === "FEMALE", "הגישה")
        .if(user.gender === "OTHER", "הגיש/ה")
        .space()
        .add("דיווח שעות חדש.")
        .done(),
      actions: [
        {
          id: NotificationAction.NAVIGATE,
          btnText: "לצפייה ואישור",
          extra: {
            to: "/user-hours",
          },
        },
      ],
    });
  },
};
